/**
 * RouteScorer turns a single google.maps DirectionsRoute into a comparable safety profile: how many
 * secure (PSR) parkings sit within the corridor, how well they cover the route at the desired spacing,
 * how many/severe incidents lie near it, and a composite safety score used to rank routes.
 *
 * All functions are pure: they take the already-loaded GeoJSON features (from a layer's markerStorage)
 * and a config object, and never touch the DOM. Geometry math is delegated to GeoUtils so the corridor
 * test stays identical to the existing radius filter.
 */

import { featureLatLng, minDistanceToPath, cumulativeDistances, densifyPath, pathLength } from "./GeoUtils";
import { incidentPenalty, filterIncidents } from "./IncidentAvoidance";

// Composite weights — the single place to tune how the safety score trades off its factors. Each term
// is bounded so no single factor can run away.
export const WEIGHTS = {
  coverage: 40, // full marks for a route fully covered by secure parking at the desired spacing
  parkingPer: 1.5,
  parkingCap: 20,
  levelPer: 5,
  incidentCap: 30,
  detourPer: 50, // a 10% detour costs 5 points
};

// PSR certification_level_id -> security weight (higher == more secure). From psr_certification_level:
// 1/2/3 (legacy numeric tiers), 4 = Partner, 5/6/7 = E-L1/E-L2/E-L3.
const LEVEL_WEIGHTS = { 1: 1, 2: 2, 3: 3, 4: 0.5, 5: 1, 6: 2, 7: 3 };

/**
 * Weight of a PSR security level. Unknown/0 gets a small positive weight (still a parking, just
 * unclassified).
 */
export function levelWeight(levelId) {
  const w = LEVEL_WEIGHTS[Number(levelId)];
  return w === undefined ? 0.5 : w;
}

/**
 * Sum of leg distances/durations for a route.
 * @returns {{distance: number, duration: number}} metres and seconds
 */
export function routeTotals(route) {
  let distance = 0;
  let duration = 0;
  (route.legs || []).forEach((leg) => {
    if (leg.distance) {
      distance += leg.distance.value;
    }
    if (leg.duration) {
      duration += leg.duration.value;
    }
  });
  return { distance, duration };
}

/**
 * PSR features whose security level is selected and that fall within the corridor of the given path.
 * Returns enriched entries with along-route and perpendicular distances (used for spacing/coverage and
 * waypoint selection).
 *
 * @param {google.maps.LatLng[]} densePath
 * @param {number[]} cum cumulative distances aligned with densePath
 * @param {google.maps.Data.Feature[]} psrFeatures
 * @param {{maxCorridorDistance: number, securityLevels: number[]}} config
 * @returns {Array<{feature: object, alongDist: number, perpDist: number, level: number}>}
 */
export function psrInCorridor(densePath, cum, psrFeatures, config) {
  const levels = config.securityLevels || [];
  const result = [];
  psrFeatures.forEach((feature) => {
    const level = Number(feature.getProperty("certification_level_id")) || 0;
    if (levels.length && !levels.includes(level)) {
      return;
    }
    const { distance, index } = minDistanceToPath(featureLatLng(feature), densePath);
    if (distance <= config.maxCorridorDistance) {
      result.push({ feature, alongDist: cum[index], perpDist: distance, level });
    }
  });
  result.sort((a, b) => a.alongDist - b.alongDist);
  return result;
}

/**
 * Coverage of a route by secure parking at the desired spacing. A stretch longer than `spacing` with no
 * PSR (including the run from start to the first PSR and from the last PSR to the destination) counts as
 * uncovered.
 *
 * @returns {{coverageScore: number, maxGap: number, gapCount: number}}
 */
export function coverage(psrEntries, totalLength, spacing) {
  if (totalLength <= 0) {
    return { coverageScore: 1, maxGap: 0, gapCount: 0 };
  }
  const anchors = [0, ...psrEntries.map((e) => e.alongDist), totalLength];
  let uncovered = 0;
  let maxGap = 0;
  let gapCount = 0;
  for (let i = 1; i < anchors.length; i++) {
    const gap = anchors[i] - anchors[i - 1];
    if (gap > maxGap) {
      maxGap = gap;
    }
    if (gap > spacing) {
      uncovered += gap - spacing;
      gapCount++;
    }
  }
  const coverageScore = Math.max(0, 1 - uncovered / totalLength);
  return { coverageScore, maxGap, gapCount };
}

/**
 * Scores one route.
 *
 * @param {google.maps.DirectionsRoute} route
 * @param {{
 *   psrFeatures: google.maps.Data.Feature[],
 *   incidentFeatures: google.maps.Data.Feature[],
 *   config: object,
 *   baselineDistance: number,
 *   nowSeconds: number,
 * }} ctx
 * @returns {object} score profile
 */
export function scoreRoute(route, ctx) {
  const { psrFeatures, incidentFeatures, config, baselineDistance, nowSeconds } = ctx;
  const densePath = densifyPath(route.overview_path, 1000);
  const cum = cumulativeDistances(densePath);
  const totalLength = pathLength(densePath);

  const totals = routeTotals(route);

  const psrEntries = psrInCorridor(densePath, cum, psrFeatures, config);
  const psrCount = psrEntries.length;
  const avgLevelWeight = psrCount
    ? psrEntries.reduce((sum, e) => sum + levelWeight(e.level), 0) / psrCount
    : 0;

  const { coverageScore, maxGap, gapCount } = coverage(psrEntries, totalLength, config.psrSpacing);

  let incidents = { penalty: 0, nearCount: 0, near: [] };
  if (config.avoidIncidents) {
    const filtered = filterIncidents(incidentFeatures, config, nowSeconds);
    incidents = incidentPenalty(densePath, filtered, config, nowSeconds);
  }

  const extraDistance = Math.max(0, totals.distance - baselineDistance);
  const detourRatio = baselineDistance > 0 ? extraDistance / baselineDistance : 0;

  const safety =
    WEIGHTS.coverage * coverageScore +
    WEIGHTS.parkingPer * Math.min(psrCount, WEIGHTS.parkingCap) +
    WEIGHTS.levelPer * avgLevelWeight -
    Math.min(incidents.penalty, WEIGHTS.incidentCap) -
    WEIGHTS.detourPer * detourRatio;

  return {
    distance: totals.distance,
    duration: totals.duration,
    psrEntries,
    psrCount,
    avgLevelWeight,
    coverageScore,
    maxGap,
    gapCount,
    incidentPenalty: incidents.penalty,
    incidentNearCount: incidents.nearCount,
    incidentNear: incidents.near,
    extraDistance,
    detourRatio,
    safety,
  };
}
